import { ENDPOINT } from "../../../../config";

const CHAT_URL = `${ENDPOINT}ai-chat/message`;

/**
 * Parses SSE lines from the buffer. Returns consumed line count's remainder in buffer.
 */
function parseSseBuffer(buffer, onEvent) {
  const parts = buffer.split("\n");
  const remainder = parts.pop() ?? "";

  for (const line of parts) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(":")) continue;

    let payload = trimmed;
    if (payload.startsWith("data:")) {
      payload = payload.slice(5).trim();
    }

    if (!payload) continue;

    try {
      onEvent(JSON.parse(payload));
    } catch {
      // ignore malformed chunks
    }
  }

  return remainder;
}

/**
 * Streams AI chat responses via POST + SSE.
 * @param {{ message: string, token: string, onEvent: (event: object) => void, signal?: AbortSignal }} options
 */
export async function streamAiChatMessage({ message, token, onEvent, signal, sessionId }) {
  const body = { message };
  if (sessionId) body.session_id = sessionId;

  const response = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (response.status === 401) {
    localStorage.clear();
    window.location.assign("/login");
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    let detail = `Request failed (${response.status})`;
    try {
      const errBody = await response.json();
      detail = errBody?.message || detail;
    } catch {
      // use default detail
    }
    throw new Error(detail);
  }

  if (!response.body) {
    throw new Error("Streaming is not supported in this browser.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    buffer = parseSseBuffer(buffer, onEvent);
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    parseSseBuffer(`${buffer}\n`, onEvent);
  }
}
